/**
 * Formats Firebase and network errors into user-friendly messages.
 * Handles Cloud Functions HttpsError codes, Firestore errors,
 * rate-limiting, and network failures.
 */
export function formatError(error) {
  if (!error) return "An unknown error occurred.";

  // Firebase Cloud Functions HttpsError — error.code is like "functions/not-found"
  const code = error.code || error.details?.code || "";

  // Rate limiting
  if (code === "functions/resource-exhausted" || code === "resource-exhausted") {
    return "Too many requests. Please wait a moment and try again.";
  }

  // Auth / permission
  if (code === "functions/unauthenticated" || code === "unauthenticated") {
    return "You must be signed in to perform this action.";
  }
  if (code === "functions/permission-denied" || code === "permission-denied") {
    return "You don't have permission to perform this action.";
  }

  // Not found
  if (code === "functions/not-found" || code === "not-found") {
    return "The requested resource was not found.";
  }

  // Already exists
  if (code === "functions/already-exists" || code === "already-exists") {
    return error.message || "This resource already exists.";
  }

  // Invalid argument
  if (code === "functions/invalid-argument" || code === "invalid-argument") {
    return error.message || "Invalid input. Please check your data and try again.";
  }

  // Precondition failed
  if (code === "functions/failed-precondition" || code === "failed-precondition") {
    return error.message || "This action cannot be completed right now.";
  }

  // Network / connectivity
  if (code === "functions/unavailable" || code === "unavailable") {
    return "Service temporarily unavailable. Please check your connection and try again.";
  }
  if (error.message?.includes("Failed to fetch") || error.message?.includes("NetworkError") || error.message?.includes("network")) {
    return "Network error. Please check your connection and try again.";
  }

  // Firestore permission denied
  if (code === "permission-denied" || error.message?.includes("Missing or insufficient permissions")) {
    return "You don't have permission to perform this action.";
  }

  // Deadline exceeded / timeout
  if (code === "functions/deadline-exceeded" || code === "deadline-exceeded") {
    return "The request timed out. Please try again.";
  }

  // Internal server error
  if (code === "functions/internal" || code === "internal") {
    return "An internal error occurred. Please try again later.";
  }

  // Fall back to the error message if it looks user-friendly (not a stack trace)
  if (error.message && error.message.length < 200 && !error.message.includes("\n")) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
}
